/**
 * Debug logger for PromptMySite widget.
 *
 * Logging is disabled by default in production.
 * Enable by setting data-debug="true" on the script tag or
 * window.PROMPTMYSITE_DEBUG = true before widget loads.
 *
 * Usage:
 *   import { debug, warn, error, createLogger } from './logger';
 *
 *   // Global logging
 *   debug('Message');
 *   warn('Warning');
 *   error('Error');
 *
 *   // Namespaced logging
 *   const log = createLogger('MyComponent');
 *   log.debug('Message');  // [PromptMySite:MyComponent] Message
 */

let debugEnabled = false;

/**
 * Log levels for filtering
 */
export type LogLevel = 'debug' | 'warn' | 'error';

/**
 * Logger interface for namespaced logging
 */
export interface Logger {
  debug: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  /** Log with explicit level */
  log: (level: LogLevel, ...args: unknown[]) => void;
}

/**
 * Check if debug mode is enabled
 */
function isDebugEnabled(): boolean {
  if (debugEnabled) return true;

  // Check window flag
  if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).PROMPTMYSITE_DEBUG === true) {
    debugEnabled = true;
    return true;
  }

  // Check script tag data attribute
  if (typeof document !== 'undefined') {
    const script = document.querySelector('script[data-workspace][data-debug="true"]');
    if (script) {
      debugEnabled = true;
      return true;
    }
  }

  return false;
}

/**
 * Format prefix with optional namespace
 */
function formatPrefix(namespace?: string): string {
  return namespace ? `[PromptMySite:${namespace}]` : '[PromptMySite]';
}

/**
 * Log a debug message (only if debug mode is enabled)
 */
export function debug(...args: unknown[]): void {
  if (isDebugEnabled()) {
    console.log('[PromptMySite]', ...args);
  }
}

/**
 * Log a warning (always logged)
 */
export function warn(...args: unknown[]): void {
  console.warn('[PromptMySite]', ...args);
}

/**
 * Log an error (always logged)
 */
export function error(...args: unknown[]): void {
  console.error('[PromptMySite]', ...args);
}

/**
 * Log at a specific level
 */
export function log(level: LogLevel, ...args: unknown[]): void {
  switch (level) {
    case 'debug':
      debug(...args);
      break;
    case 'warn':
      warn(...args);
      break;
    case 'error':
      error(...args);
      break;
  }
}

/**
 * Create a namespaced logger for a component or module.
 *
 * @param namespace The component/module name to prefix logs with
 * @returns Logger interface with debug/warn/error methods
 *
 * @example
 * const log = createLogger('ChatPanel');
 * log.debug('Initialized');  // [PromptMySite:ChatPanel] Initialized
 * log.error('Failed', err);  // [PromptMySite:ChatPanel] Failed Error: ...
 */
export function createLogger(namespace: string): Logger {
  const prefix = formatPrefix(namespace);

  return {
    debug: (...args: unknown[]) => {
      if (isDebugEnabled()) {
        console.log(prefix, ...args);
      }
    },
    warn: (...args: unknown[]) => {
      console.warn(prefix, ...args);
    },
    error: (...args: unknown[]) => {
      console.error(prefix, ...args);
    },
    log: (level: LogLevel, ...args: unknown[]) => {
      switch (level) {
        case 'debug':
          if (isDebugEnabled()) console.log(prefix, ...args);
          break;
        case 'warn':
          console.warn(prefix, ...args);
          break;
        case 'error':
          console.error(prefix, ...args);
          break;
      }
    },
  };
}

/**
 * Explicitly enable debug mode
 */
export function enableDebug(): void {
  debugEnabled = true;
}

/**
 * Explicitly disable debug mode
 */
export function disableDebug(): void {
  debugEnabled = false;
}

/**
 * Check current debug mode status
 */
export function isDebug(): boolean {
  return isDebugEnabled();
}
